import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { 
  collection, getDocs, query, orderBy, where, 
  doc, getDoc, Timestamp, limit, setDoc, deleteDoc
} from 'firebase/firestore';
import Sidebar from '../components/Sidebar';
import MobileHeader from '../components/MobileHeader';
import { showError } from '../utils/notifications';
import ModalDialog from '../components/ModalDialog';
import * as XLSX from 'xlsx';
import { logDeliveryConfirmation } from '../services/auditService';

function RechargeHistory() {
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [recharges, setRecharges] = useState([]);
  const [activeSection, setActiveSection] = useState('recharges');
  const [todayMotoTotal, setTodayMotoTotal] = useState(0);
  const [todayAutoTotal, setTodayAutoTotal] = useState(0);
  const [todayTicketsTotal, setTodayTicketsTotal] = useState(0);
  
  // Estados para totales por método de pago
  const [todayEfectivoTotal, setTodayEfectivoTotal] = useState(0);
  const [todayTransferenciaTotal, setTodayTransferenciaTotal] = useState(0);
  const [todayDepositoTotal, setTodayDepositoTotal] = useState(0);

  
  // Estados para modal
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'alert'
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'confirm'
  });  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  

  
  const navigate = useNavigate();

  const [entregas, setEntregas] = useState({});
  const [users, setUsers] = useState([]);
  
  // Estado para búsqueda por CA (solo para Supervisor)
  const [caSearchFilter, setCaSearchFilter] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      const usuariosRef = collection(db, 'usuarios');
      const q = query(usuariosRef);
      const querySnapshot = await getDocs(q);
      const usersList = [];
      querySnapshot.forEach((doc) => {
        usersList.push({
          email: doc.id, // o doc.data().email
          nombre: doc.data().nombre || doc.id
        });
      });
      setUsers(usersList);
    } catch (error) {
      console.error("Error al cargar usuarios:", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const getUserName = useCallback((email) => {
    const user = users.find(u => u.email === email);
    return user ? user.nombre : email;
  }, [users]);

  // Formateo de timestamp para mostrar fecha y hora en formato DD/MM/YYYY, HH:MM
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      let date;
      if (typeof timestamp === 'object' && timestamp.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        return String(timestamp);
      }
      
      // Formatear como DD/MM/YYYY, HH:MM
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      
      return `${day}/${month}/${year}, ${hours}:${minutes}`;    } catch (error) {
      console.error("Error formateando fecha:", error);
      return String(timestamp);
    }
  };

// Calcular el total de recargas desde los datos consolidados mostrados en la tabla
const calculateTodayTotal = useCallback(() => {
  try {
    console.log("Calculando totales desde los datos consolidados de la tabla");
    
    let motoTotal = 0;
    let autoTotal = 0;
    let totalTickets = 0;
    let efectivoTotal = 0;
    let transferenciaTotal = 0;
    let depositoTotal = 0;
    
    // Usar los mismos datos que se muestran en la tabla
    recharges.forEach((recharge) => {
      if (recharge.isConsolidated) {
        // Para recargas consolidadas, usar los totales ya calculados
        motoTotal += recharge.totalMotoValue || 0;
        autoTotal += recharge.totalAutoValue || 0;
        totalTickets += (recharge.totalMotoTickets || 0) + (recharge.totalAutoTickets || 0);
        // Sumar totales por método de pago
        efectivoTotal += recharge.totalEfectivoValue || 0;
        transferenciaTotal += recharge.totalTransferenciaValue || 0;
        depositoTotal += recharge.totalDepositoValue || 0;
      } else {
        // Para recargas individuales
        if (recharge.operationType === 'recarga_tickets') {
          totalTickets += recharge.ticketsAdded || 0;
          const valor = recharge.price || 0;
          const metodoPagoRaw = recharge.paymentMethod || recharge.metodoPago || 'efectivo';
          const metodoPago = String(metodoPagoRaw).toLowerCase();
          
          if (recharge.vehicleType === 'moto') {
            motoTotal += valor;
          } else if (recharge.vehicleType === 'auto') {
            autoTotal += valor;
          }
          
          // Sumar por método de pago
          if (metodoPago === 'transferencia') {
            transferenciaTotal += valor;
          } else if (metodoPago === 'deposito' || metodoPago === 'depósito') {
            depositoTotal += valor;
          } else {
            efectivoTotal += valor; // Por defecto consideramos efectivo
          }
        }
      }
    });
    
    console.log("Totales calculados desde tabla:", { motoTotal, autoTotal, totalTickets, efectivoTotal, transferenciaTotal, depositoTotal });
    
    setTodayMotoTotal(motoTotal);
    setTodayAutoTotal(autoTotal);
    setTodayTicketsTotal(totalTickets);
    setTodayEfectivoTotal(efectivoTotal);
    setTodayTransferenciaTotal(transferenciaTotal);
    setTodayDepositoTotal(depositoTotal);
  } catch (error) {
    console.error("Error al calcular total desde tabla:", error);
  }
}, [recharges]);



  // Función para consolidar recargas por ejecutor (para Admin y Recaudador)
  const consolidateRechargesByExecutor = useCallback((rechargesList) => {
    // Consolidar solo si es Admin o Recaudador (NO consolidar para Supervisor)
    if (userRole !== 'Admin' && userRole !== 'Recaudador') {
      return rechargesList; // No consolidar si no es Admin ni Recaudador (incluye Supervisor)
    }
    
    console.log("Consolidación - Admin o Recaudador:", userRole === 'Admin' || userRole === 'Recaudador');

    const consolidated = {};
    
    rechargesList.forEach(recharge => {
      const executor = recharge.createdBy || 'Sistema';
      const rechargeDate = recharge.date?.seconds ? new Date(recharge.date.seconds * 1000) : new Date();
      const dateKey = rechargeDate.toLocaleDateString('es-ES'); // Formato DD/MM/YYYY
      
      // Crear clave única combinando ejecutor y fecha
      const consolidationKey = `${executor}_${dateKey}`;
      
      if (!consolidated[consolidationKey]) {
        consolidated[consolidationKey] = {
          id: `consolidated-${executor}-${dateKey.replace(/\//g, '-')}`,
          createdBy: executor,
          executorName: executor, // Nombre del ejecutor para mostrar
          formattedDate: `Consolidado del ${dateKey} - ${executor}`,
          consolidationDate: dateKey,
          isConsolidated: true,
          totalMotoTickets: 0,
          totalAutoTickets: 0,
          totalMotoValue: 0,
          totalAutoValue: 0,
          totalBalanceRecharges: 0,
          totalBalanceValue: 0,
          // Totales por método de pago
          totalEfectivoValue: 0,
          totalTransferenciaValue: 0,
          totalDepositoValue: 0,
          rechargeCount: 0,
          // Mantener información del primer registro para mostrar
          numberCa: recharge.numberCa,
          userName: recharge.userName,
          operationType: 'consolidated'
        };
      }
      
      const consolidatedItem = consolidated[consolidationKey];
      consolidatedItem.rechargeCount++;
      
      if (recharge.operationType === 'recarga_tickets') {
        const valor = recharge.price || 0;
        const metodoPagoRaw = recharge.paymentMethod || recharge.metodoPago || 'efectivo';
        const metodoPago = String(metodoPagoRaw).toLowerCase();
        
        if (recharge.vehicleType === 'moto') {
          consolidatedItem.totalMotoTickets += recharge.ticketsAdded || 0;
          consolidatedItem.totalMotoValue += valor;
        } else if (recharge.vehicleType === 'auto') {
          consolidatedItem.totalAutoTickets += recharge.ticketsAdded || 0;
          consolidatedItem.totalAutoValue += valor;
        }
        
        // Consolidar por método de pago
        if (metodoPago === 'transferencia') {
          consolidatedItem.totalTransferenciaValue += valor;
        } else if (metodoPago === 'deposito' || metodoPago === 'depósito') {
          consolidatedItem.totalDepositoValue += valor;
        } else {
          consolidatedItem.totalEfectivoValue += valor; // Por defecto efectivo
        }
      } else {
        // Recarga de balance
        consolidatedItem.totalBalanceRecharges++;
        consolidatedItem.totalBalanceValue += recharge.amount || 0;
        
        // Para balances también clasificar por método de pago si está disponible
        const metodoPagoRaw = recharge.paymentMethod || recharge.metodoPago || 'efectivo';
        const metodoPago = String(metodoPagoRaw).toLowerCase();
        const valor = recharge.amount || 0;
        if (metodoPago === 'transferencia') {
          consolidatedItem.totalTransferenciaValue += valor;
        } else if (metodoPago === 'deposito' || metodoPago === 'depósito') {
          consolidatedItem.totalDepositoValue += valor;
        } else {
          consolidatedItem.totalEfectivoValue += valor;
        }
      }
    });
    
    const consolidatedArray = Object.values(consolidated)
      .sort((a, b) => {
        // Primero ordenar por fecha (más reciente primero)
        const dateA = new Date(a.consolidationDate.split('/').reverse().join('-'));
        const dateB = new Date(b.consolidationDate.split('/').reverse().join('-'));
        if (dateB - dateA !== 0) return dateB - dateA;
        
        // Luego por ejecutor
        return a.executorName.localeCompare(b.executorName);
      });
    
    console.log("Consolidación completada:");
    consolidatedArray.forEach(item => {
      console.log(`  - ${item.formattedDate}: ${item.rechargeCount} recargas, $${(item.totalMotoValue + item.totalAutoValue + item.totalBalanceValue).toFixed(2)}`);
    });
    
    return consolidatedArray;
  }, [userRole]);

  // Función mejorada para obtener recargas
  const fetchRecharges = useCallback(async (filterOptions = {}, userRoleOverride = null) => {
    try {
      console.log("Obteniendo recargas con las siguientes opciones:", filterOptions);
      console.log("Usuario actual:", auth.currentUser?.email);
      
      // Usar el rol explícito pasado como parámetro o el rol del estado
      const effectiveUserRole = userRoleOverride || userRole;
      console.log("Rol del usuario:", effectiveUserRole);
      
      // Construir la consulta base
      let queryConstraints = [orderBy('date', 'desc')];      // Lógica específica para diferentes roles
      if (effectiveUserRole === 'Supervisor' && auth.currentUser) {
        console.log("Aplicando filtro de supervisor para:", auth.currentUser.email);
        queryConstraints.push(where('createdBy', '==', auth.currentUser.email));      } else if (effectiveUserRole === 'Recaudador') {
        console.log("Aplicando filtros de Recaudador - puede ver recargas de todos los supervisores");
        // Para Recaudador: si no hay filtros específicos de fecha, mostrar todas las recargas del día actual por defecto
        if (!filterOptions.startDate && !filterOptions.endDate && !filterOptions.fechaDocumento) {
          const today = new Date();
          const dd = today.getDate().toString().padStart(2, '0');
          const mm = (today.getMonth() + 1).toString().padStart(2, '0');
          const yyyy = today.getFullYear();
          
          // Crear ambos formatos de fecha para cubrir inconsistencias en la BD
          const todayFormatted1 = `${dd}-${mm}-${yyyy}`;  // Con ceros: "24-05-2025"
          const todayFormatted2 = `${today.getDate()}-${today.getMonth() + 1}-${yyyy}`;  // Sin ceros: "24-5-2025"
          
          console.log("Recaudador: buscando recargas con ambos formatos de fecha:", todayFormatted1, "y", todayFormatted2);
          
          // En lugar de usar un filtro where específico, vamos a usar filtro por timestamp de fecha
          // para capturar todas las recargas del día independientemente del formato de fechaDocumento
          const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
          const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
          
          // Usar la misma lógica que Reports.js - SIN ajustes de zona horaria
          console.log("Recaudador: filtro día actual:", startOfDay.toLocaleString('es-ES'), "hasta", endOfDay.toLocaleString('es-ES'));
          
          queryConstraints = [
            where('date', '>=', Timestamp.fromDate(startOfDay)),
            where('date', '<=', Timestamp.fromDate(endOfDay)),
            orderBy('date', 'desc')
          ];
        } else {
          console.log("Recaudador: usando filtros personalizados proporcionados");
          // Cuando hay filtros personalizados, aplicarlos junto con la lógica normal
        }
        // No filtrar por createdBy - el Recaudador puede ver todas las recargas
      }

      // Aplicar filtros de fecha personalizados (para todos los roles)
      if (effectiveUserRole !== 'None') {
        if (filterOptions.startDate) {
          // Si hay filtros personalizados, limpiar configuración por defecto del Recaudador si existe
          if (effectiveUserRole === 'Recaudador') {
            queryConstraints = [orderBy('date', 'desc')];
          }
          
          const startDateParts = filterOptions.startDate.split('-');
          const startDateObj = new Date(parseInt(startDateParts[0]), parseInt(startDateParts[1]) - 1, parseInt(startDateParts[2]));
          startDateObj.setHours(0, 0, 0, 0);
          
          // Usar la misma lógica que Reports.js - SIN ajustes de zona horaria
          queryConstraints.push(where('date', '>=', Timestamp.fromDate(startDateObj)));
          console.log("Aplicando filtro fecha inicio:", startDateObj.toLocaleDateString('es-ES'));
        }
        
        if (filterOptions.endDate) {
          const endDateParts = filterOptions.endDate.split('-');
          const endDateObj = new Date(parseInt(endDateParts[0]), parseInt(endDateParts[1]) - 1, parseInt(endDateParts[2]));
          endDateObj.setHours(23, 59, 59, 999);
          
          // Usar la misma lógica que Reports.js - SIN ajustes de zona horaria
          queryConstraints.push(where('date', '<=', Timestamp.fromDate(endDateObj)));
          console.log("Aplicando filtro fecha fin:", endDateObj.toLocaleDateString('es-ES'));
        }
        
        // Aplicar filtro por fechaDocumento si se especifica
        if (filterOptions.fechaDocumento) {
          // Preservar filtros de rol existentes
          const roleFilters = queryConstraints.filter(constraint => 
            constraint.toString().includes('createdBy')
          );
          
          queryConstraints = [
            ...roleFilters,
            where('fechaDocumento', '==', filterOptions.fechaDocumento),
            orderBy('date', 'desc')
          ];
          console.log("Aplicando filtro por fechaDocumento:", filterOptions.fechaDocumento);
        }
      }
      
      // Aplicar filtro por CA (disponible para todos los roles)
      if (filterOptions.numberCa) {
        queryConstraints.push(where('numberCa', '==', filterOptions.numberCa));
      }
        
      // Aplicar filtro por admin/usuario que ejecutó la recarga (solo para Admin y Recaudador del día actual)
      if (filterOptions.admin && (effectiveUserRole === 'Admin' || effectiveUserRole === 'Recaudador')) {
        queryConstraints.push(where('createdBy', '==', filterOptions.admin));
      }
      
      // Aplicar filtro por tipo de operación (disponible para todos los roles)
      if (filterOptions.operationType) {
        if (filterOptions.operationType === 'recarga_tickets') {
          queryConstraints.push(where('operationType', '==', 'recarga_tickets'));
        } else if (filterOptions.operationType === 'recarga_balance') {
          // Para recargas de balance, filtramos donde operationType no existe o no es 'recarga_tickets'
          queryConstraints.push(where('operationType', '!=', 'recarga_tickets'));
        }
      }      // Limitar resultados para mejor rendimiento
      // Para Recaudador, permitir más resultados para ver todas las recargas del día
      if (effectiveUserRole === 'Recaudador') {
        queryConstraints.push(limit(1000)); // Límite mucho más alto para Recaudador
      } else {
        queryConstraints.push(limit(1000));
      }
        // Consultar ambas colecciones en paralelo
      const [snapInternas, snapRecharge] = await Promise.all([
        getDocs(query(collection(db, 'RechargeInternas'), ...queryConstraints)),
        getDocs(query(collection(db, 'Recharge'),         ...queryConstraints)),
      ]);
      console.log(`RechargeInternas: ${snapInternas.size} | Recharge: ${snapRecharge.size}`);

      // Deduplicar por fingerprint
      const _fp = (data) =>
        `${data.fechaDocumento}|${data.numberCa}|${data.vehicleType}|${data.ticketsAdded}|${data.tokenticked}|${data.vendorId}`;
      const _huellas = new Set();
      const taggedDocs = [
        ...snapInternas.docs.map(d => ({ doc: d, source: 'RI' })),
        ...snapRecharge.docs.map(d => ({ doc: d, source: 'SD' })),
      ];

      const rechargesList = [];
      taggedDocs.forEach(({ doc, source }) => {
        const data = doc.data();
        const fp = _fp(data);
        if (_huellas.has(fp)) return;
        _huellas.add(fp);

        // Filtro adicional para supervisores
        if (effectiveUserRole === 'Supervisor' && auth.currentUser && data.createdBy !== auth.currentUser.email) {
          return;
        }

        rechargesList.push({
          id: doc.id,
          source,
          ...data,
          formattedDate: formatTimestamp(data.date), // Usar el formato completo con fecha y hora
          // Aseguramos que tengamos estos valores
          userName: data.userName || 'N/A',
          amount: data.amount || 0,
          previousBalance: data.previousBalance || 0,
          newBalance: data.newBalance || 0,
          createdBy: data.createdBy || 'Sistema',
          numberCa: data.numberCa || 'N/A',
          clientIP: data.clientIP || 'N/A',
          fechaDocumento: data.fechaDocumento || formatTimestamp(data.date).split(',')[0],
          // Campos específicos para recargas de tickets
          operationType: data.operationType || null,
          vehicleType: data.vehicleType || null,
          ticketsAdded: data.ticketsAdded || 0,
          price: data.price || 0,
          previousTicketsMoto: data.previousTicketsMoto || 0,
          newTicketsMoto: data.newTicketsMoto || 0,
          previousTicketsAuto: data.previousTicketsAuto || 0,
          newTicketsAuto: data.newTicketsAuto || 0,
          paymentMethod: data.paymentMethod || 'efectivo',
          notes: data.notes || null,
          status: data.status || null
        });
      });
      
      console.log(`Procesados ${rechargesList.length} registros para mostrar`);
      
      // Aplicar consolidación para Admin y Recaudador (NO para Supervisor)
      const finalRechargesList = consolidateRechargesByExecutor(rechargesList);
      setRecharges(finalRechargesList);
      
      // Calcular el total de recargas para el día actual después de actualizar la tabla
      calculateTodayTotal();
    } catch (error) {
      console.error("Error al cargar historial de recargas:", error);
      showError("Error", "No se pudo cargar el historial de recargas");
    }
  }, [calculateTodayTotal, userRole, consolidateRechargesByExecutor]);

  // Verificar directamente si hay recargas para el usuario actual
  const checkUserHasRecharges = useCallback(async () => {
    if (!auth.currentUser) return;
    
    try {
      const rechargesRef = collection(db, 'RechargeInternas');
      const q = query(
        rechargesRef, 
        where('createdBy', '==', auth.currentUser.email),
        limit(1)
      );
      
      const querySnapshot = await getDocs(q);
      const hasRecharges = !querySnapshot.empty;
      
      if (!hasRecharges && userRole === 'Supervisor') {
        console.log("El usuario no tiene recargas registradas en el sistema");
        setAlertModal({
          isOpen: true,
          title: "Información",
          message: "No se encontraron recargas ejecutadas por usted. Como supervisor, solo puede ver las recargas que usted mismo ha realizado.",
          type: "info"
        });
      }
      
      return hasRecharges;
    } catch (error) {
      console.error("Error al verificar si el usuario tiene recargas:", error);
      return false;
    }
  }, [userRole]); // Added userRole to dependency array
  
  // Funciones para modal de selección de columnas - FUNCIÓN ELIMINADA (no se usa)
  // const openColumnModal = () => { ... };

  // Exportar recargas a Excel con las columnas actuales de la tabla
  const exportToExcel = () => {
    try {
      // Preparar los datos para el Excel con las mismas columnas de la tabla
      const exportData = recharges.map(recharge => {
        const row = {
          'Fecha y Hora': recharge.formattedDate,
          'Tipo': recharge.isConsolidated ? 'Consolidado' : 
                  recharge.operationType === 'recarga_tickets' ? 'Tickets' : 'Balance',
          'Valor': recharge.isConsolidated ? 
                  `${recharge.rechargeCount} recargas consolidadas` :
                  recharge.operationType === 'recarga_tickets' ? 
                  `${recharge.ticketsAdded} tickets ${recharge.vehicleType} - $${(recharge.price || 0).toFixed(2)}` :
                  `$${(recharge.amount || 0).toFixed(2)}`,
          'Valor a entregar': recharge.isConsolidated ? 
                            (recharge.totalMotoValue + recharge.totalAutoValue + recharge.totalBalanceValue).toFixed(2) :
                            recharge.operationType === 'recarga_tickets' ? 
                            (recharge.price || 0).toFixed(2) : 
                            (recharge.amount || 0).toFixed(2),
          'Ejecutado por': recharge.isConsolidated ? 
                          getUserName(recharge.executorName) : 
                          getUserName(recharge.createdBy),
          'Entrega confirmada': recharge.isConsolidated ? 
                               (entregas[recharge.id]?.confirmado ? 'SÍ' : 'NO') : 
                               'N/A'
        };
        return row;
      });

      // Calcular totales
      const totalMonto = recharges.reduce((sum, recharge) => {
        if (recharge.isConsolidated) {
          return sum + (recharge.totalMotoValue + recharge.totalAutoValue + recharge.totalBalanceValue);
        } else if (recharge.operationType === 'recarga_tickets') {
          return sum + (recharge.price || 0);
        } else {
          return sum + (recharge.amount || 0);
        }
      }, 0);

      // Agregar fila de totales
      const totalsRow = {
        'Fecha y Hora': 'TOTALES:',
        'Tipo': '',
        'Valor': `${recharges.length} registros`,
        'Valor a entregar': totalMonto.toFixed(2),
        'Ejecutado por': '',
        'Entrega confirmada': ''
      };

      const dataWithTotals = [...exportData, totalsRow];

      // Crear el archivo Excel
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(dataWithTotals);

      // Configurar anchos de columnas
      ws['!cols'] = [
        { wch: 20 }, // Fecha y Hora
        { wch: 15 }, // Tipo
        { wch: 40 }, // Valor
        { wch: 18 }, // Valor a entregar
        { wch: 25 }, // Ejecutado por
        { wch: 18 }  // Entrega confirmada
      ];

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, 'Recargas');

      // Generar nombre del archivo
      const now = new Date();
      const timestamp = `${now.getDate().toString().padStart(2, '0')}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getFullYear()}_${now.getHours().toString().padStart(2, '0')}-${now.getMinutes().toString().padStart(2, '0')}`;
      const fileName = `recargas_${userRole}_${timestamp}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(wb, fileName);

      // Mostrar mensaje de éxito
      setAlertModal({
        isOpen: true,
        title: "✅ Exportación Exitosa",
        message: `Se ha descargado "${fileName}" con ${exportData.length} registros.\n\nColumnas incluidas:\n• Fecha y Hora\n• Tipo de recarga\n• Valor detallado\n• Valor a entregar\n• Ejecutado por\n• Estado de entrega\n\nTotal a entregar: $${totalMonto.toFixed(2)}`,
        type: "success"
      });

    } catch (error) {
      console.error("Error al exportar a Excel:", error);
      setAlertModal({
        isOpen: true,
        title: "❌ Error en Exportación",
        message: "No se pudo exportar el archivo Excel. Por favor, inténtelo nuevamente.",
        type: "error"
      });
    }
  };

  // Cargar estado de entregas desde Firestore
  const loadEntregasFromFirestore = useCallback(async () => {
    try {
      const entregasRef = collection(db, 'entregas');
      const querySnapshot = await getDocs(entregasRef);
      const entregasData = {};
      
      querySnapshot.forEach((doc) => {
        entregasData[doc.id] = {
          confirmado: doc.data().confirmado || false,
          fechaConfirmacion: doc.data().fechaConfirmacion,
          confirmadoPor: doc.data().confirmadoPor
        };
      });
      
      setEntregas(entregasData);
      console.log("Estados de entrega cargados desde Firestore:", entregasData);
    } catch (error) {
      console.error("Error al cargar entregas:", error);
    }
  }, []);

  // Guardar estado de entrega en Firestore
  const saveEntregaToFirestore = useCallback(async (rechargeId, isConfirmed) => {
    try {
      const entregaRef = doc(db, 'entregas', rechargeId);
      
      if (isConfirmed) {
        // Guardar confirmación
        await setDoc(entregaRef, {
          confirmado: true,
          fechaConfirmacion: new Date(),
          confirmadoPor: auth.currentUser?.email || 'unknown',
          rechargeId: rechargeId
        });
        console.log(`Entrega confirmada para: ${rechargeId}`);
      } else {
        // Eliminar confirmación
        await deleteDoc(entregaRef);
        console.log(`Confirmación eliminada para: ${rechargeId}`);
      }
    } catch (error) {
      console.error("Error al guardar entrega:", error);
      showError("Error", "No se pudo guardar el estado de entrega");
    }
  }, []);

  // Manejar cambio en checkbox de entrega
  const handleEntregaChange = useCallback(async (rechargeId, currentState) => {
    // Si ya está confirmado, no permitir cambios
    if (currentState) {
      setAlertModal({
        isOpen: true,
        title: "🔒 Entrega Ya Confirmada",
        message: "Esta entrega ya ha sido confirmada y no se puede modificar.\n\nPor seguridad, las confirmaciones de entrega son permanentes.",
        type: "warning"
      });
      return;
    }
    
    // Solo permitir marcar como entregado (no desmarcar)
    // Encontrar la recarga para mostrar información específica
    const recharge = recharges.find(r => r.id === rechargeId);
    const ejecutor = recharge ? getUserName(recharge.executorName || recharge.createdBy) : 'Usuario';
    const monto = recharge ? (recharge.totalMotoValue + recharge.totalAutoValue + recharge.totalBalanceValue).toFixed(2) : '0.00';
    
    setConfirmModal({
      isOpen: true,
      title: "🚨 Confirmar Entrega de Dinero",
      message: `¿Está seguro de confirmar la entrega de dinero?\n\n📋 Detalles:\n• Ejecutor: ${ejecutor}\n• Monto: $${monto}\n• Fecha: ${new Date().toLocaleDateString('es-ES')}\n\n⚠️ Esta acción quedará registrada permanentemente en el sistema.\n🔒 Una vez confirmada, NO se podrá deshacer.\n\n✅ Al confirmar, se guardará:\n• Quién confirmó la entrega\n• Fecha y hora exacta\n• Monto entregado`,
      type: "confirm",
      onConfirm: async () => {
        // Actualizar estado local inmediatamente
        setEntregas(prev => ({
          ...prev,
          [rechargeId]: {
            ...prev[rechargeId],
            confirmado: true
          }
        }));
        
        // Guardar en Firestore
        await saveEntregaToFirestore(rechargeId, true);
        
        // Registrar en logs de auditoría
        try {
          await logDeliveryConfirmation(
            { email: auth.currentUser?.email, rol: userRole },
            rechargeId,
            recharge,
            monto,
            {
              confirmedFromPage: 'recharge_history',
              consolidatedEntry: recharge?.isConsolidated || false,
              executorName: ejecutor
            }
          );
          console.log("✅ Confirmación de entrega registrada en logs de auditoría");
        } catch (auditError) {
          console.error("❌ Error al registrar en auditoría:", auditError);
          // No bloquear la operación si falla la auditoría
        }
        
        // Mostrar mensaje de éxito
        setAlertModal({
          isOpen: true,
          title: "✅ Entrega Confirmada",
          message: `La entrega de $${monto} para ${ejecutor} ha sido registrada exitosamente.\n\nConfirmado por: ${auth.currentUser?.email}\nFecha: ${new Date().toLocaleString('es-ES')}\n\n🔒 Esta confirmación es permanente y no se puede modificar.\n\n📝 Acción registrada en logs de auditoría.`,
          type: "success"
        });
        
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  }, [recharges, getUserName, saveEntregaToFirestore, userRole]);

  // Cargar entregas al iniciar
  useEffect(() => {
    if (user) {
      loadEntregasFromFirestore();
    }
  }, [user, loadEntregasFromFirestore]);

  // Efecto principal para cargar los datos
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          // Try multiple lookup strategies for user verification
          let userDoc = null;
          let userData = null;
          
          // Strategy 1: Look up by email (current approach)
          try {
            const userDocRef = doc(db, 'usuarios', currentUser.email);
            userDoc = await getDoc(userDocRef);
            if (userDoc.exists()) {
              userData = userDoc.data();
            }
          } catch (error) {
            console.log("Lookup by email failed:", error);
          }
          
          // Strategy 2: If email lookup fails, try lookup by UID
          if (!userData) {
            try {
              const userDocRefByUID = doc(db, 'usuarios', currentUser.uid);
              userDoc = await getDoc(userDocRefByUID);
              if (userDoc.exists()) {
                userData = userDoc.data();
                console.log("Found user by UID:", currentUser.uid);
              }
            } catch (error) {
              console.log("Lookup by UID failed:", error);
            }
          }
          
          // Strategy 3: Query the collection to find user by email field
          if (!userData) {
            try {
              const usuariosRef = collection(db, 'usuarios');
              const q = query(usuariosRef, where('email', '==', currentUser.email));
              const querySnapshot = await getDocs(q);
              
              if (!querySnapshot.empty) {
                querySnapshot.forEach((doc) => {
                  userData = doc.data();
                  console.log("Found user by email field query:", doc.id);
                });
              }
            } catch (error) {
              console.log("Lookup by email field query failed:", error);
            }
          }
          
          // Strategy 4: For CA users, try to find by any CA-related field
          if (!userData) {
            try {
              const usuariosRef = collection(db, 'usuarios');
              const q = query(usuariosRef, where('rol', '==', 'CA'));
              const querySnapshot = await getDocs(q);
              
              querySnapshot.forEach((doc) => {
                const caData = doc.data();
                // Check if this CA user matches current user
                if (caData.email === currentUser.email || 
                    caData.uid === currentUser.uid ||
                    doc.id === currentUser.email ||
                    doc.id === currentUser.uid) {
                  userData = caData;
                  console.log("Found CA user:", doc.id);
                }
              });
            } catch (error) {
              console.log("CA lookup failed:", error);
            }
          }
          
          if (!userData) {
            // If still no user found, check if this might be a valid CA that needs to be created
            console.log("No user profile found. User details:", {
              email: currentUser.email,
              uid: currentUser.uid,
              displayName: currentUser.displayName
            });
            
            showError("Usuario no encontrado", 
              "Tu perfil no existe en el sistema. Si eres un Controlador Autorizado, contacta al administrador para que configure tu cuenta.");
            await auth.signOut();
            navigate('/login');
            return;
          }
          
          // El usuario existe, continuar normalmente
          setUser(currentUser);
          
          // Almacenar el rol del usuario (de la base de datos)
          const userRoleValue = userData.rol || 'Usuario';
          
          // Establecer el rol del usuario antes de cualquier operación que dependa de él
          setUserRole(userRoleValue);
          console.log("Rol del usuario establecido a:", userRoleValue);
            // Verificar si el usuario tiene permisos para esta página
          if (userRoleValue !== 'Admin' && userRoleValue !== 'Supervisor' && userRoleValue !== 'CA' && userRoleValue !== 'Recaudador') {
            showError("Acceso denegado", "No tienes permisos para acceder a esta página");
            navigate('/dashboard');
            return;
          }
          
          // Opcionalmente, guardar en localStorage para persistencia
          localStorage.setItem('userRole', userRoleValue);
          
          // Esperar a que el estado se actualice antes de continuar
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Cargar datos con el rol actualizado
          
          // Para evitar condiciones de carrera, pasamos explícitamente el rol en vez de confiar en el estado
          // Cargar solo las recargas del día actual al iniciar
          const today = new Date();
          const todayString = today.getFullYear() + '-' + 
                             String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                             String(today.getDate()).padStart(2, '0');
          
          console.log("🔄 Carga inicial: Solo recargas del día actual:", todayString);
          
          await fetchRecharges({
            startDate: todayString,
            endDate: todayString
          }, userRoleValue);
          await calculateTodayTotal(userRoleValue);
          
          // Si el usuario es supervisor, verificar si tiene recargas
          if (userRoleValue === 'Supervisor') {
            setTimeout(() => {
              checkUserHasRecharges();
            }, 500); // Darle tiempo a la interfaz para renderizar
          }
        } catch (error) {
          console.error("Error al verificar usuario:", error);
          showError("Error", "Ocurrió un problema al verificar tu perfil");
          await auth.signOut();
          navigate('/login');
        }
      } else {
        navigate('/login');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, fetchRecharges, calculateTodayTotal, checkUserHasRecharges]);

  // Filtrar recargas por CA (solo para Supervisor)
  const filteredRecharges = useMemo(() => {
    if (userRole !== 'Supervisor' || !caSearchFilter) {
      return recharges;
    }
    
    // Filtrar por CA si hay un filtro activo
    const searchTerm = caSearchFilter.toLowerCase().trim();
    return recharges.filter(recharge => {
      const caNumber = (recharge.numberCa || '').toString().toLowerCase();
      const userName = (recharge.userName || '').toString().toLowerCase();
      return caNumber.includes(searchTerm) || userName.includes(searchTerm);
    });
  }, [recharges, caSearchFilter, userRole]);

  // Remove unused handleRechargeSubmit function if it's not needed
  // Otherwise keep it for future use
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100">


      {/* Mobile Header */}
      <MobileHeader 
        title="Historial de Recargas" 
        userEmail={user?.email}
        userRole={userRole}
        toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />
      
      {/* Add top spacing for mobile to prevent content from being hidden under header */}
      <div className="md:hidden h-14"></div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Overlay for mobile menu */}
        {isMobileMenuOpen && (
          <div 
            className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-10"
            onClick={() => setIsMobileMenuOpen(false)}
          ></div>
        )}
        
        {/* Sidebar */}
        <Sidebar 
          activeSection={activeSection} 
          setActiveSection={setActiveSection} 
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />

        {/* Main Content */}
        <div className="flex-1 overflow-auto">          {/* Top Header (desktop only) */}
          <header className="bg-white shadow px-6 py-4 hidden md:block">
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold text-gray-900">Historial de Recargas</h1>
              <div className="text-right flex items-center">
                <div className="mr-4">
                  <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  <p className="text-xs text-gray-500">{userRole || "Usuario"}</p>
                </div>

                <button 
                  onClick={async () => {
                    try {
                      // Limpiar también el rol al cerrar sesión
                      localStorage.removeItem('userRole');
                      await auth.signOut();
                      navigate('/login');
                    } catch (error) {
                      console.error("Error al cerrar sesión:", error);
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white text-xs px-3 py-1 rounded"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="p-6">            {/* Totales del día */}
            <div className="bg-white shadow rounded-lg p-6 mb-6">              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-700">Resumen del Día - {new Date().toLocaleDateString('es-ES')}</h3>
                  {userRole === 'Supervisor' && (
                    <p className="text-xs text-blue-600 mt-1">
                      Solo se muestran las recargas realizadas por usted
                    </p>
                  )}

                </div>
                <button 
                  onClick={calculateTodayTotal}
                  className="text-blue-500 hover:text-blue-700"
                  title="Actualizar totales"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">🏍️ Recargas motos hoy</p>
                  <p className="text-2xl font-bold text-blue-700">${todayMotoTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {userRole === 'Supervisor' ? 'Sus recargas del día' : 
                     userRole === 'Recaudador' ? 'Total de todos los supervisores del día' : 
                     'Total del día actual'}
                  </p>
                </div>                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">🚗 Recargas autos hoy</p>
                  <p className="text-2xl font-bold text-green-700">${todayAutoTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {userRole === 'Supervisor' ? 'Sus recargas del día' : 
                     userRole === 'Recaudador' ? 'Total de todos los supervisores del día' : 
                     'Total del día actual'}
                  </p>
                </div>                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">💰 Total recargas hoy</p>
                  <p className="text-2xl font-bold text-purple-700">${(todayMotoTotal + todayAutoTotal).toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {userRole === 'Supervisor' ? 'Suma de sus recargas' : 
                     userRole === 'Recaudador' ? 'Suma total de todos los supervisores del día' : 
                     'Suma total del día'}
                  </p>
                </div>
              </div>
              
              {/* Segunda fila con métodos de pago y tickets */}
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">💵 Efectivo hoy</p>
                  <p className="text-2xl font-bold text-yellow-700">${todayEfectivoTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Pagos en efectivo
                  </p>
                </div>
                
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">🏦 Transferencia hoy</p>
                  <p className="text-2xl font-bold text-indigo-700">${todayTransferenciaTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Pagos por transferencia
                  </p>
                </div>

                <div className="bg-cyan-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">🏛️ Depósito hoy</p>
                  <p className="text-2xl font-bold text-cyan-700">${todayDepositoTotal.toFixed(2)}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Pagos por depósito
                  </p>
                </div>
                
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700 mb-1">🎫 Total tickets hoy</p>
                  <p className="text-2xl font-bold text-orange-700">{todayTicketsTotal}</p>                  <p className="text-xs text-gray-500 mt-1">
                    {userRole === 'Supervisor' ? 'Tickets en sus recargas' : 
                     userRole === 'Recaudador' ? 'Tickets de todos los supervisores del día' : 
                     'Tickets recargados hoy'}
                  </p>
                </div>
              </div>
            </div>

            {/* Botón de Descarga - Oculto para Supervisor */}
            {userRole !== 'Supervisor' && (
              <div className="bg-white shadow rounded-lg p-4 mb-6">
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={exportToExcel}
                    disabled={recharges.length === 0}
                    className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg text-sm font-medium flex items-center space-x-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Descargar Excel</span>
                  </button>
                </div>
              </div>
            )}

            {/* Buscador por CA - Solo para Supervisor */}
            {userRole === 'Supervisor' && (
              <div className="bg-white shadow rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-4">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    🔍 Buscar por CA o Nombre:
                  </label>
                  <input
                    type="text"
                    value={caSearchFilter}
                    onChange={(e) => setCaSearchFilter(e.target.value)}
                    placeholder="Ej: CA51, Juan Pérez..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {caSearchFilter && (
                    <button
                      onClick={() => setCaSearchFilter('')}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Historial de Recargas */}
            <div className="bg-white shadow rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center space-x-3">
                  <h2 className="text-lg font-semibold text-gray-700">Historial de Recargas</h2>
                  {(userRole === 'Admin' || userRole === 'Recaudador') && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                      </svg>
                      Consolidado
                    </span>
                  )}
                </div>
                <span className="text-sm text-gray-500">
                  {userRole === 'Supervisor' ? filteredRecharges.length : recharges.length} registros
                </span>
                </div>
                
                {/* Vista de Tarjetas para Móvil */}
                <div className="md:hidden space-y-4">
                  {(userRole === 'Supervisor' ? filteredRecharges : recharges).length === 0 ? (
                    <div className="bg-white rounded-lg shadow p-6 text-center text-gray-500">
                      <p>No hay recargas registradas.</p>
                      {userRole === 'Supervisor' && (
                        <div className="mt-4">
                          <p className="text-xs text-blue-600 mb-2">
                            Como supervisor, solo puede ver las recargas que usted ha realizado.
                          </p>
                          <button
                            onClick={checkUserHasRecharges}
                            className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded"
                          >
                            Verificar Permisos
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    (userRole === 'Supervisor' ? filteredRecharges : recharges).map((recharge) => (
                      <div key={recharge.id} className="bg-white rounded-lg shadow p-4 border border-gray-200">
                        {/* Header de la tarjeta */}
                        <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-200">
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900 mb-1">
                              {recharge.formattedDate}
                            </div>
                            <div className="text-xs text-gray-500">
                              {recharge.isConsolidated ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  Consolidado
                                </span>
                              ) : recharge.operationType === 'recarga_tickets' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  🎫 Tickets
                                </span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  💰 Balance
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Información específica para Supervisor */}
                        {userRole === 'Supervisor' && (
                          <div className="space-y-2 mb-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">CA:</span>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {recharge.numberCa || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Nombre:</span>
                              <span className="text-sm font-medium text-gray-900">
                                {recharge.userName || 'N/A'}
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">Tipo Vehículo:</span>
                              {recharge.vehicleType === 'moto' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  🏍️ Moto
                                </span>
                              ) : recharge.vehicleType === 'auto' ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  🚗 Auto
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">N/A</span>
                              )}
                            </div>
                            {recharge.operationType === 'recarga_tickets' && (
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-600">Tickets Recargados:</span>
                                <span className="text-sm font-semibold text-purple-600">
                                  {recharge.ticketsAdded || 0} tickets
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Información de Valor */}
                        <div className="mb-3 pb-3 border-b border-gray-200">
                          {recharge.isConsolidated ? (
                            <div className="space-y-2">
                              <div className="text-sm font-semibold text-blue-600">
                                📊 {recharge.rechargeCount} recargas consolidadas
                              </div>
                              {recharge.totalMotoTickets > 0 && (
                                <div className="text-xs text-purple-600">
                                  🏍️ {recharge.totalMotoTickets} tickets moto - ${recharge.totalMotoValue.toFixed(2)}
                                </div>
                              )}
                              {recharge.totalAutoTickets > 0 && (
                                <div className="text-xs text-green-600">
                                  🚗 {recharge.totalAutoTickets} tickets auto - ${recharge.totalAutoValue.toFixed(2)}
                                </div>
                              )}
                              <div className="text-lg font-bold text-green-700 mt-2">
                                Total: ${(recharge.totalMotoValue + recharge.totalAutoValue + recharge.totalBalanceValue).toFixed(2)}
                              </div>
                            </div>
                          ) : recharge.operationType === 'recarga_tickets' ? (
                            <div className="space-y-1">
                              <div className="text-sm font-semibold text-purple-600">
                                {recharge.ticketsAdded} tickets {recharge.vehicleType === 'moto' ? '🏍️' : '🚗'}
                              </div>
                              <div className="text-lg font-bold text-gray-900">
                                ${recharge.price?.toFixed(2) || '0.00'}
                              </div>
                              <div className="text-xs font-medium">
                                {(recharge.paymentMethod || recharge.metodoPago) === 'transferencia' ? (
                                  <span className="text-indigo-600">🏦 Transferencia</span>
                                ) : (recharge.paymentMethod || recharge.metodoPago) === 'deposito' || (recharge.paymentMethod || recharge.metodoPago) === 'depósito' ? (
                                  <span className="text-cyan-600">🏛️ Depósito</span>
                                ) : (
                                  <span className="text-yellow-600">💵 Efectivo</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="text-lg font-bold text-green-600">
                              ${recharge.amount.toFixed(2)}
                            </div>
                          )}
                        </div>

                        {/* Footer de la tarjeta */}
                        <div className="flex items-center justify-between">
                          <div className="text-xs text-gray-500">
                            <span className="font-medium">Ejecutado por:</span>{' '}
                            {recharge.isConsolidated ? getUserName(recharge.executorName) : getUserName(recharge.createdBy)}
                          </div>
                          {userRole !== 'Supervisor' && recharge.isConsolidated && (
                            <label className="flex items-center text-xs font-semibold text-blue-700 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!entregas[recharge.id]?.confirmado}
                                onChange={() => handleEntregaChange(recharge.id, entregas[recharge.id]?.confirmado)}
                                className="form-checkbox h-4 w-4 text-green-600 mr-1"
                              />
                              Entrega
                            </label>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {/* Vista de Tabla para Desktop */}
                <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha y Hora</th>
                      {userRole === 'Supervisor' && (
                        <>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CA</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo Vehículo</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tickets</th>
                        </>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor</th>
                      {userRole !== 'Supervisor' && (
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Valor a entregar</th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Origen</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ejecutado por</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(userRole === 'Supervisor' ? filteredRecharges : recharges).map((recharge) => (
                      <tr key={recharge.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{recharge.formattedDate}</td>
                        {userRole === 'Supervisor' && (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {recharge.numberCa || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {recharge.userName || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {recharge.vehicleType === 'moto' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                  🏍️ Moto
                                </span>
                              ) : recharge.vehicleType === 'auto' ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  🚗 Auto
                                </span>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {recharge.operationType === 'recarga_tickets' ? (
                                <span className="text-purple-600">{recharge.ticketsAdded || 0} tickets</span>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {recharge.isConsolidated ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z"/>
                              </svg>
                              Consolidado
                            </span>
                          ) : recharge.operationType === 'recarga_tickets' ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                              Tickets
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                              </svg>
                              Balance
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          {recharge.isConsolidated ? (
                            <div className="space-y-2">
                              <div className="font-medium text-blue-600">
                                📊 {recharge.rechargeCount} recargas consolidadas
                              </div>
                              {recharge.totalMotoTickets > 0 && (
                                <div className="text-xs text-purple-600">
                                  🏍️ {recharge.totalMotoTickets} tickets moto - ${recharge.totalMotoValue.toFixed(2)}
                                </div>
                              )}
                              {recharge.totalAutoTickets > 0 && (
                                <div className="text-xs text-green-600">
                                  🚗 {recharge.totalAutoTickets} tickets auto - ${recharge.totalAutoValue.toFixed(2)}
                                </div>
                              )}
                              {recharge.totalBalanceRecharges > 0 && (
                                <div className="text-xs text-orange-600">
                                  💰 {recharge.totalBalanceRecharges} recargas balance - ${recharge.totalBalanceValue.toFixed(2)}
                                </div>
                              )}
                              <div className="mt-2 pt-2 border-t border-gray-200">
                                <div className="text-xs font-medium text-yellow-600">
                                  💵 Efectivo: ${(recharge.totalEfectivoValue || 0).toFixed(2)}
                                </div>
                                <div className="text-xs font-medium text-indigo-600">
                                  🏦 Transferencia: ${(recharge.totalTransferenciaValue || 0).toFixed(2)}
                                </div>
                                <div className="text-xs font-medium text-cyan-600">
                                  🏛️ Depósito: ${(recharge.totalDepositoValue || 0).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          ) : recharge.operationType === 'recarga_tickets' ? (
                            <div className="space-y-1">
                              <div className="font-medium text-purple-600">
                                {recharge.ticketsAdded} tickets {recharge.vehicleType === 'moto' ? '🏍️' : '🚗'}
                              </div>
                              <div className="text-xs text-gray-500">
                                ${recharge.price?.toFixed(2) || '0.00'}
                              </div>
                              <div className="text-xs font-medium mt-1">
                                {(recharge.paymentMethod || recharge.metodoPago) === 'transferencia' ? (
                                  <span className="text-indigo-600">🏦 Transferencia</span>
                                ) : (recharge.paymentMethod || recharge.metodoPago) === 'deposito' || (recharge.paymentMethod || recharge.metodoPago) === 'depósito' ? (
                                  <span className="text-cyan-600">🏛️ Depósito</span>
                                ) : (
                                  <span className="text-yellow-600">💵 Efectivo</span>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="font-medium text-green-600">
                              ${recharge.amount.toFixed(2)}
                            </div>
                          )}
                        </td>
                        {userRole !== 'Supervisor' && (
                          <td className="px-6 py-4 text-center">
                            {recharge.isConsolidated ? (
                              <div className="flex flex-col items-center">
                                <span className="text-xl font-extrabold text-green-700 mb-2">
                                  ${(recharge.totalMotoValue + recharge.totalAutoValue + recharge.totalBalanceValue).toFixed(2)}
                                </span>
                                <label className="flex items-center text-base font-semibold text-blue-700 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={!!entregas[recharge.id]?.confirmado}
                                    onChange={() => handleEntregaChange(recharge.id, entregas[recharge.id]?.confirmado)}
                                    className="form-checkbox h-6 w-6 text-green-600 mr-2"
                                  />
                                  Confirmar entrega de caja
                                </label>
                              </div>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {recharge.isConsolidated ? (
                            <span className="text-gray-400 text-xs">—</span>
                          ) : recharge.source === 'SD' ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-orange-100 text-orange-700">SD</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-indigo-100 text-indigo-700">RI</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="truncate max-w-32" title={recharge.createdBy}>
                            {recharge.isConsolidated ? getUserName(recharge.executorName) : getUserName(recharge.createdBy)}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {recharges.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-10 text-center text-gray-500">
                          <div className="space-y-4">
                            <p>No hay recargas registradas.</p>
                            {userRole === 'Supervisor' && (
                              <div>
                                <p className="text-xs text-blue-600 mb-2">
                                  Como supervisor, solo puede ver las recargas que usted ha realizado.
                                  Si cree que debería ver recargas aquí, verifique los filtros aplicados 
                                  o contacte al administrador del sistema.
                                </p>
                                <button
                                  onClick={checkUserHasRecharges}
                                  className="bg-blue-500 hover:bg-blue-600 text-white text-xs px-3 py-1 rounded"
                                >
                                  Verificar Permisos
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </main>
        </div>
      </div>
        {/* Modales */}
      
      <ModalDialog
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal(prev => ({...prev, isOpen: false}))}
        type={confirmModal.type}
      />
      
      <ModalDialog
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        message={alertModal.message}
        onConfirm={() => setAlertModal(prev => ({...prev, isOpen: false}))}
        type={alertModal.type}
      />
    </div>
  );
}

export default memo(RechargeHistory);
